import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Plus, ShieldCheck, AlertTriangle, UserCheck, ExternalLink, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  name: '', email: '', phone: '', child_name: '', child_age: '',
  address: '', notes: '', tags: '',
};

/**
 * Reusable manual "Add Customer" dialog. Also doubles as the "Convert prospect
 * to customer" flow when `inquiryId` + `requirePin` are provided.
 *
 * Props
 *  - open: boolean
 *  - onOpenChange: (open: boolean) => void
 *  - onCreated: (customer) => void               // called on success
 *  - initialData?: Partial<form>                 // pre-fill (used in conversion)
 *  - inquiryId?: string                          // if set -> conversion endpoint
 *  - requirePin?: boolean                        // forces PIN field (default: conversion mode)
 *  - title?: string                              // dialog title override
 *  - description?: string                        // dialog description override
 *  - submitLabel?: string                        // primary button label
 */
export const AddCustomerDialog = ({
  open, onOpenChange, onCreated,
  initialData = null, inquiryId = null,
  requirePin = false,
  title, description, submitLabel,
  onResolveExisting = null,
}) => {
  const isConversion = Boolean(inquiryId);
  const pinRequired = isConversion || requirePin;
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Email conflict detection (deleted-customer reactivation flow)
  const [emailCheck, setEmailCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [choice, setChoice] = useState('reactivate');

  // Pre-fill when opening
  useEffect(() => {
    if (open) {
      setForm({ ...emptyForm, ...(initialData || {}) });
      setPin('');
      setEmailCheck(null);
      setReactivateOpen(false);
      setChoice('reactivate');
    }
  }, [open, initialData]);

  const checkEmail = async () => {
    const email = (form.email || '').trim().toLowerCase();
    if (isConversion || !email || !email.includes('@')) { setEmailCheck(null); return; }
    setChecking(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/customer-email-check`, {
        params: { email }, withCredentials: true,
      });
      setEmailCheck(res.data);
      if (res.data?.exists && res.data?.deleted) { setChoice('reactivate'); setReactivateOpen(true); }
    } catch { setEmailCheck(null); }
    finally { setChecking(false); }
  };

  const goToProfile = (email) => {
    onOpenChange(false);
    setReactivateOpen(false);
    if (onResolveExisting) onResolveExisting(email);
    else navigate(`/crm/customers?email=${encodeURIComponent(email)}`);
  };

  const doReactivate = async () => {
    const email = (form.email || '').trim().toLowerCase();
    setReactivating(true);
    try {
      await axios.post(`${API_URL}/api/admin/customers/${encodeURIComponent(email)}/reactivate`, {}, { withCredentials: true });
      toast.success('Customer reactivated — all history restored');
      goToProfile(email);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reactivate customer');
    } finally { setReactivating(false); }
  };

  const cust = emailCheck?.customer || {};
  const deletedDate = cust.deleted_at
    ? new Date(cust.deleted_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '';

  const handleSubmit = async () => {
    if (!form.name?.trim()) { toast.error('Name is required'); return; }
    if (!form.email?.trim() || !form.email.includes('@')) {
      toast.error('Valid email required'); return;
    }
    if (!isConversion && emailCheck?.exists) {
      if (emailCheck.active) { toast.error('A customer with this email already exists — open their profile instead'); return; }
      if (emailCheck.deleted) { setReactivateOpen(true); return; }
    }
    if (pinRequired && !pin.trim()) {
      toast.error('Employee PIN required'); return;
    }
    setSubmitting(true);
    try {
      const tagsArr = (form.tags || '').split(',').map((t) => t.trim()).filter(Boolean);
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: (form.phone || '').trim(),
        address: (form.address || '').trim(),
        child_name: (form.child_name || '').trim(),
        child_age: form.child_age ? Number(form.child_age) : null,
        notes: (form.notes || '').trim(),
        tags: tagsArr,
      };
      if (pinRequired) payload.pin = pin.trim();

      let res;
      if (isConversion) {
        res = await axios.post(
          `${API_URL}/api/admin/contact-inquiries/${inquiryId}/convert`,
          payload, { withCredentials: true },
        );
        toast.success(`${payload.name} added to customers`);
      } else {
        res = await axios.post(
          `${API_URL}/api/admin/customers`, payload, { withCredentials: true },
        );
        toast.success(`${payload.name} added to the customer list`);
      }
      onOpenChange(false);
      setForm(emptyForm);
      setPin('');
      if (onCreated) onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create customer');
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => { if (!submitting) onOpenChange(o); }}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-customer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            <Plus className="w-5 h-5 text-[#FF3B6B]" /> {title || (isConversion ? 'Convert to Customer' : 'Add Customer')}
          </DialogTitle>
          <DialogDescription>
            {description || (isConversion
              ? 'Pre-filled from the prospect inquiry. Adjust any details, enter your PIN, and add this person to the customer list.'
              : "Create a customer manually (e.g. a phone-in or walk-in). They'll appear in the customer list immediately and can be linked to a future booking.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Full Name *</Label>
              <Input value={form.name} onChange={(e) => set('name', e.target.value)}
                placeholder="Jane Smith" className="mt-1" data-testid="add-customer-name" />
            </div>
            <div>
              <Label className="text-xs">Email *</Label>
              <Input type="email" value={form.email}
                onChange={(e) => { set('email', e.target.value); if (emailCheck) setEmailCheck(null); }}
                onBlur={checkEmail}
                placeholder="jane@example.com" className="mt-1" data-testid="add-customer-email" />
              {checking && <p className="text-[11px] text-[#6B6B7A] mt-1">Checking email…</p>}
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)}
                placeholder="(905) 555-1234" className="mt-1" data-testid="add-customer-phone" />
            </div>
            <div>
              <Label className="text-xs">Address</Label>
              <Input value={form.address} onChange={(e) => set('address', e.target.value)}
                placeholder="123 Maple St, Oakville" className="mt-1" data-testid="add-customer-address" />
            </div>
            <div>
              <Label className="text-xs">Child's Name</Label>
              <Input value={form.child_name} onChange={(e) => set('child_name', e.target.value)}
                placeholder="Lila" className="mt-1" data-testid="add-customer-child-name" />
            </div>
            <div>
              <Label className="text-xs">Child's Age</Label>
              <Input type="number" min="1" max="18" value={form.child_age}
                onChange={(e) => set('child_age', e.target.value)}
                placeholder="7" className="mt-1" data-testid="add-customer-child-age" />
            </div>
          </div>

          {/* Active duplicate → offer to open their profile */}
          {!isConversion && emailCheck?.exists && emailCheck.active && (
            <div className="rounded-xl border border-[#E8A80E]/40 bg-[#FFFBEB] p-3" data-testid="add-customer-active-conflict">
              <p className="text-sm text-[#92610E] flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-none" />
                A customer with this email already exists. Would you like to open their profile instead?
              </p>
              <Button type="button" size="sm" onClick={() => goToProfile((form.email || '').trim().toLowerCase())}
                className="mt-2 rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="open-existing-customer">
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open Existing Customer
              </Button>
            </div>
          )}

          {/* Soft-deleted duplicate → offer to reactivate */}
          {!isConversion && emailCheck?.exists && emailCheck.deleted && (
            <div className="rounded-xl border border-[#FF3B6B]/30 bg-[#FFF1F4] p-3" data-testid="add-customer-deleted-conflict">
              <p className="text-sm text-[#9F1239] flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-none" />
                This email belongs to a previously deleted customer{deletedDate ? ` (deleted ${deletedDate})` : ''}.
              </p>
              <Button type="button" size="sm" onClick={() => { setChoice('reactivate'); setReactivateOpen(true); }}
                className="mt-2 rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="review-reactivate-btn">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Review &amp; reactivate
              </Button>
            </div>
          )}

          <div>
            <Label className="text-xs">Tags (comma-separated)</Label>
            <Input value={form.tags} onChange={(e) => set('tags', e.target.value)}
              placeholder="vip, walk-in" className="mt-1" data-testid="add-customer-tags" />
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)}
              placeholder="Birthday in June, prefers afternoon slots…"
              rows={3} className="mt-1" data-testid="add-customer-notes" />
          </div>

          {pinRequired && (
            <div className="rounded-xl border border-[#E8E5DF] bg-[#FFFDF8] p-3">
              <Label className="text-xs flex items-center gap-1 text-[#1E1E24]">
                <ShieldCheck className="w-3.5 h-3.5 text-[#FF3B6B]" /> Your employee PIN *
              </Label>
              <Input type="password" inputMode="numeric" autoComplete="off"
                value={pin} onChange={(e) => setPin(e.target.value)}
                placeholder="••••" className="mt-1 tracking-widest"
                data-testid="add-customer-pin" />
              <p className="text-[11px] text-[#6B6B7A] mt-1">
                Used to credit this conversion to you for performance tracking.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
            className="rounded-full" data-testid="add-customer-cancel">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || (!isConversion && emailCheck?.exists)}
            className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
            data-testid="add-customer-submit">
            {submitting ? 'Saving…' : (submitLabel || (isConversion ? 'Convert & Add' : 'Add Customer'))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Reactivate deleted customer modal */}
    <Dialog open={reactivateOpen} onOpenChange={(o) => { if (!reactivating) setReactivateOpen(o); }}>
      <DialogContent className="bg-white max-w-md" data-testid="reactivate-customer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            <AlertTriangle className="w-5 h-5 text-[#E8A80E]" /> Email already on file
          </DialogTitle>
          <DialogDescription>We found an existing (deleted) customer with this email.</DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-[#E8E5DF] bg-[#FFFDF8] p-3 text-sm space-y-1.5" data-testid="reactivate-customer-details">
          <div className="flex gap-2"><span className="w-16 text-[#6B6B7A]">Name</span><span className="font-medium text-[#1E1E24]">{cust.name || '—'}</span></div>
          <div className="flex gap-2"><span className="w-16 text-[#6B6B7A]">Email</span><span className="font-medium text-[#1E1E24] break-all">{cust.email || '—'}</span></div>
          <div className="flex gap-2"><span className="w-16 text-[#6B6B7A]">Phone</span><span className="font-medium text-[#1E1E24]">{cust.phone || '—'}</span></div>
          <div className="flex gap-2"><span className="w-16 text-[#6B6B7A]">Status</span><span className="font-medium text-red-600">Deleted{deletedDate ? ` on ${deletedDate}` : ''}</span></div>
        </div>

        <p className="text-sm font-medium text-[#1E1E24] mt-1">What would you like to do?</p>
        <div className="space-y-2">
          <label className={`flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer ${choice === 'reactivate' ? 'border-[#FF3B6B] bg-[#FFF1F4]' : 'border-[#E8E5DF]'}`}>
            <input type="radio" name="reactivate-choice" className="mt-1 accent-[#FF3B6B]" checked={choice === 'reactivate'} onChange={() => setChoice('reactivate')} data-testid="reactivate-option-reactivate" />
            <span>
              <span className="block text-sm font-semibold text-[#1E1E24] flex items-center gap-1.5"><UserCheck className="w-4 h-4 text-[#06D6A0]" /> Reactivate this customer</span>
              <span className="block text-xs text-[#6B6B7A] mt-0.5">Restores the account with all historical data (bookings, loyalty points, invoices). Keeps the same email.</span>
            </span>
          </label>
          <label className={`flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer ${choice === 'cancel' ? 'border-[#1E1E24] bg-[#FAF8F4]' : 'border-[#E8E5DF]'}`}>
            <input type="radio" name="reactivate-choice" className="mt-1 accent-[#1E1E24]" checked={choice === 'cancel'} onChange={() => setChoice('cancel')} data-testid="reactivate-option-cancel" />
            <span className="text-sm font-semibold text-[#1E1E24]">Cancel</span>
          </label>
        </div>

        <DialogFooter>
          <Button
            onClick={() => { if (choice === 'reactivate') doReactivate(); else setReactivateOpen(false); }}
            disabled={reactivating}
            className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white w-full"
            data-testid="reactivate-continue">
            {reactivating ? 'Reactivating…' : 'Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default AddCustomerDialog;
